import React, { useState, useEffect, useMemo, useCallback } from 'react';
import Grid from '@material-ui/core/Grid';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import TextField from '@material-ui/core/TextField';
import Button from '@material-ui/core/Button';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import Checkbox from '@material-ui/core/Checkbox';
import MenuItem from '@material-ui/core/MenuItem';
import Card from '@material-ui/core/Card';
import CardHeader from '@material-ui/core/CardHeader';
import Divider from '@material-ui/core/Divider';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import CircularProgress from '@material-ui/core/CircularProgress';
import { makeStyles } from '@material-ui/core/styles';
import { datasetRecord as API, form } from '../../../api';
import Swal from 'sweetalert2';
import { toThaiYear } from '../../../utils/thaiDate';

const useStyles = makeStyles((theme) => ({
    root: {
        padding: theme.spacing(3),
    },
    paper: {
        padding: theme.spacing(3),
        marginBottom: theme.spacing(3),
    },
    list: {
        width: 300,
        height: 400,
        backgroundColor: theme.palette.background.paper,
        overflow: 'auto',
        border: '1px solid #ccc',
    },
    button: {
        margin: theme.spacing(0.5, 0),
    },
    transferListContainer: {
        alignItems: 'center',
        justifyContent: 'center',
    },
}));

function not(a, b) {
    return a.filter((value) => b.indexOf(value) === -1);
}

function intersection(a, b) {
    return a.filter((value) => b.indexOf(value) !== -1);
}

function union(a, b) {
    return [...a, ...not(b, a)];
}

// Extracted Component for performance (rerender-memo)
const TransferList = React.memo(({ title, items, currentFilterText, setFilterFunc, enableDrag, loading, checked, handleToggle, handleToggleAll, handleDragStart, handleDragOver, handleDrop, draggedItemIndex, numberOfChecked }) => {
    const classes = useStyles();

    // Filter items based on passed filter text (derived state inside memoized component)
    const displayedItems = useMemo(() => items.filter(i => (i.operatorName && i.operatorName.toLowerCase().includes(currentFilterText.toLowerCase())) || (i.taxNo && i.taxNo.includes(currentFilterText))), [items, currentFilterText]);

    return (
        <Card>
            <CardHeader
                className={classes.cardHeader}
                avatar={
                    <Checkbox
                        onClick={handleToggleAll(displayedItems)}
                        checked={numberOfChecked(displayedItems) === displayedItems.length && displayedItems.length !== 0}
                        indeterminate={numberOfChecked(displayedItems) !== displayedItems.length && numberOfChecked(displayedItems) !== 0}
                        disabled={displayedItems.length === 0}
                        inputProps={{ 'aria-label': 'all items selected' }}
                    />
                }
                title={title}
                subheader={`${numberOfChecked(displayedItems)}/${displayedItems.length} selected`}
            />
            <div style={{ padding: '0 16px 8px 16px' }}>
                <TextField
                    fullWidth
                    placeholder="ค้นหา..."
                    value={currentFilterText}
                    onChange={(e) => setFilterFunc(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                />
            </div>
            <Divider />
            {loading ? (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
                    <CircularProgress />
                </div>
            ) : (
                <List className={classes.list} dense component="div" role="list">
                    {displayedItems.map((value, index) => {
                        const labelId = `transfer-list-all-item-${value.id}-label`;

                        return (
                            <ListItem
                                key={value.id}
                                role="listitem"
                                button
                                onClick={handleToggle(value)}
                                draggable={enableDrag}
                                onDragStart={enableDrag ? handleDragStart(index) : undefined}
                                onDragOver={enableDrag ? handleDragOver(index) : undefined}
                                onDrop={enableDrag ? handleDrop(index) : undefined}
                                style={enableDrag && draggedItemIndex === index ? { opacity: 0.5 } : {}}
                            >
                                <ListItemIcon>
                                    <Checkbox
                                        checked={checked.indexOf(value) !== -1}
                                        tabIndex={-1}
                                        disableRipple
                                        inputProps={{ 'aria-labelledby': labelId }}
                                    />
                                </ListItemIcon>
                                <ListItemText id={labelId} primary={value.operatorName} secondary={value.taxNo} />
                            </ListItem>
                        );
                    })}
                    <ListItem />
                </List>
            )}
        </Card>
    )
});

export default function ExportPdfPage() {
    const classes = useStyles();
    const [year, setYear] = useState(new Date().getFullYear());
    const [quarter, setQuarter] = useState(1);
    const [formVersion, setFormVersion] = useState(2);
    const [operators, setOperators] = useState([]);
    const [templates, setTemplates] = useState([]);
    const [eventList, setEventList] = useState([]);
    const [loadingResult, setLoadingResult] = useState(false);

    // Transfer List State
    const [checked, setChecked] = useState([]);
    const [left, setLeft] = useState([]);
    const [right, setRight] = useState([]);

    // Template Selection State
    const [selectedTemplates, setSelectedTemplates] = useState([]);

    const leftChecked = intersection(checked, left);
    const rightChecked = intersection(checked, right);

    useEffect(() => {
        fetchTemplates();
        fetchEvents();
    }, []);

    useEffect(() => {
        if (year && quarter) {
            const loadData = async () => {
                setLoadingResult(true);
                try {
                    // Parallel fetching for better performance/UX synchrony
                    const operatorPromise = fetchOperators();

                    // Find current event to get version
                    const currentEvent = eventList.find(e => e.year === year && e.quarter === quarter);
                    const version = currentEvent && currentEvent.formVersion ? currentEvent.formVersion : 2;
                    setFormVersion(version);

                    // Clear templates before fetching to indicate loading/change
                    setTemplates([]);

                    const templatePromise = fetchTemplates(version);

                    await Promise.all([operatorPromise, templatePromise]);
                } catch (err) {
                    // console.error("Error loading data", err);
                } finally {
                    setLoadingResult(false);
                }
            };
            loadData();
        }
    }, [year, quarter, eventList]);

    const fetchEvents = async () => {
        const res = await form.getEvents();
        if (res && res.data) {
            setEventList(res.data);
            if (res.data.length > 0) {
                // Try to find active event first
                const activeEvent = res.data.find(e => e.isActive === 'y');
                if (activeEvent) {
                    setYear(activeEvent.year);
                    setQuarter(activeEvent.quarter);
                    setFormVersion(activeEvent.formVersion || 2);
                } else {
                    // Fallback to the first event in the list
                    setYear(res.data[0].year);
                    setQuarter(res.data[0].quarter);
                    setFormVersion(res.data[0].formVersion || 2);
                }
            }
        }
    }

    const fetchTemplates = async (version = 2) => {
        try {
            // Assuming version 2 for now, or make it selectable if needed
            const res = await API.getHeadsForExcelExport(version);
            if (res && Array.isArray(res)) {
                // Deduplicate by 'no'
                const unique = [];
                const seen = new Set();
                const has225 = res.some(t => t.no === '2.2.5');

                for (const t of res) {
                    // Start change: Check if 'no' exists and hasn't been seen
                    if (t.no && !seen.has(t.no)) {
                        // Specific Request: Group 2.2.5.x under 2.2.5
                        // If we have 2.2.5, skip 2.2.5.x
                        if (has225 && t.no.startsWith('2.2.5.') && t.no.length > '2.2.5'.length) {
                            continue;
                        }

                        seen.add(t.no);
                        unique.push(t);
                    }
                    // End change
                }

                // Sort by 'no' (version sort)
                const sorted = unique.sort((a, b) => {
                    const partsA = (a.no || '').toString().split('.').map(x => parseInt(x, 10));
                    const partsB = (b.no || '').toString().split('.').map(x => parseInt(x, 10));

                    for (let i = 0; i < Math.max(partsA.length, partsB.length); i++) {
                        const valA = isNaN(partsA[i]) ? 0 : partsA[i];
                        const valB = isNaN(partsB[i]) ? 0 : partsB[i];
                        if (valA !== valB) return valA - valB;
                    }
                    return 0;
                });

                setTemplates(sorted);
                // Reset selected templates when templates change
                setSelectedTemplates([]);
            } else {
                setTemplates([]);
            }
        } catch (error) {
            // console.error("Error fetching templates:", error);
        }
    };

    const fetchOperators = async () => {
        try {
            const res = await API.getSubmittedOperators(year, quarter);
            if (res && Array.isArray(res)) {
                setOperators(res);
                setLeft(res);
                setRight([]);
            } else {
                setOperators([]);
                setLeft([]);
                setRight([]);
            }
        } catch (error) {
            // console.error("Error fetching operators:", error);
        }
    };

    const handleToggle = useCallback((value) => () => {
        const currentIndex = checked.indexOf(value);
        const newChecked = [...checked];

        if (currentIndex === -1) {
            newChecked.push(value);
        } else {
            newChecked.splice(currentIndex, 1);
        }

        setChecked(newChecked);
    }, [checked]);

    const numberOfChecked = useCallback((items) => intersection(checked, items).length, [checked]);

    const handleToggleAll = useCallback((items) => () => {
        if (numberOfChecked(items) === items.length) {
            setChecked(not(checked, items));
        } else {
            setChecked(union(checked, items));
        }
    }, [checked, numberOfChecked]);

    const handleCheckedRight = () => {
        setRight(right.concat(leftChecked));
        setLeft(not(left, leftChecked));
        setChecked(not(checked, leftChecked));
    };

    const handleCheckedLeft = () => {
        setLeft(left.concat(rightChecked));
        setRight(not(right, rightChecked));
        setChecked(not(checked, rightChecked));
    };

    const handleTemplateToggle = (value) => () => {
        const currentIndex = selectedTemplates.indexOf(value);
        const newChecked = [...selectedTemplates];

        if (currentIndex === -1) {
            newChecked.push(value);
        } else {
            newChecked.splice(currentIndex, 1);
        }

        setSelectedTemplates(newChecked);
    };

    const handleSelectAllTemplates = () => {
        if (selectedTemplates.length === templates.length) {
            setSelectedTemplates([]);
        } else {
            setSelectedTemplates(templates.map(t => t.no));
        }
    };

    const handleExport = async () => {
        if (right.length === 0) {
            Swal.fire('คำเตือน', 'โปรดเลือกอย่างน้อย 1 ผู้ประกอบการ', 'warning');
            return;
        }
        if (selectedTemplates.length === 0) {
            Swal.fire('คำเตือน', 'โปรดเลือกอย่างน้อย 1 ค่าชี้วัดคุณภาพบริการ', 'warning');
            return;
        }

        const payload = {
            OperatorIds: right.map(op => op.id),
            Year: year,
            Quarter: quarter,
            TemplateNos: selectedTemplates
        };

        try {
            Swal.fire({
                title: 'กำลังส่งคำขอ...',
                text: 'ระบบกำลังเริ่มดำเนินการ',
                allowOutsideClick: false,
                onBeforeOpen: () => {
                    Swal.showLoading();
                }
            });

            // 1. Trigger the background job
            const response = await API.exportPdfMultiple(payload);

            if (response && response.jobId) {
                const jobId = response.jobId;

                Swal.fire({
                    title: 'กำลังเตรียมข้อมูลรายงาน...',
                    text: 'กรุณารอสักครู่ (อาจใช้เวลาหลายนาที)...',
                    allowOutsideClick: false,
                    onBeforeOpen: () => {
                        Swal.showLoading();
                    }
                });

                // 2. Poll for status
                const checkStatus = async () => {
                    try {
                        const statusRes = await API.getExportPdfStatus(jobId);

                        if (statusRes.status === 'Completed') {
                            Swal.close();

                            // 3. Download the actual PDF
                            const blob = await API.downloadExportPdf(jobId);
                            if (blob) {
                                const url = window.URL.createObjectURL(new Blob([blob]));
                                const link = document.createElement('a');
                                link.href = url;
                                link.setAttribute('download', 'รายงานเปรียบเทียบค่าชี้วัดคุณภาพบริการระหว่างบริษัท.pdf');
                                document.body.appendChild(link);
                                link.click();
                                link.parentNode.removeChild(link);
                            }
                        } else if (statusRes.status === 'Processing') {
                            setTimeout(checkStatus, 3000); // Poll again in 3 seconds
                        } else {
                            Swal.fire('Error', 'ไม่สามารถออกรายงานได้ ' + (statusRes.error || ''), 'error');
                        }
                    } catch (pollError) {
                        Swal.fire('Error', 'เกิดข้อผิดพลาดในการตรวจสอบสถานะ', 'error');
                    }
                };

                checkStatus(); // Start polling

            } else {
                Swal.fire('Error', 'ไม่สามารถเริ่มการออกรายงานได้', 'error');
            }
        } catch (error) {
            Swal.fire('Error', 'เกิดข้อผิดพลาดในการส่งคำขอ', 'error');
        }
    };

    const handleExportExcel = async () => {
        if (right.length === 0) {
            Swal.fire('คำเตือน', 'โปรดเลือกอย่างน้อย 1 ผู้ประกอบการ', 'warning');
            return;
        }
        if (selectedTemplates.length === 0) {
            Swal.fire('คำเตือน', 'โปรดเลือกอย่างน้อย 1 ค่าชี้วัดคุณภาพบริการ', 'warning');
            return;
        }

        const payload = {
            OperatorIds: right.map(op => op.id),
            Year: year,
            Quarter: quarter,
            TemplateNos: selectedTemplates
        };

        try {
            Swal.fire({
                title: 'กำลังออกรายงาน Excel...',
                allowOutsideClick: false,
                onBeforeOpen: () => {
                    Swal.showLoading();
                }
            });

            const blob = await API.exportExcelMultiple(payload);

            if (blob) {
                const url = window.URL.createObjectURL(new Blob([blob]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', 'รายงานเปรียบเทียบค่าชี้วัดคุณภาพบริการระหว่างบริษัท.xlsx');
                document.body.appendChild(link);
                link.click();
                link.parentNode.removeChild(link);
                Swal.close();
            } else {
                Swal.fire('Error', 'ไม่สามารถออกรายงานได้', 'error');
            }
        } catch (error) {
            Swal.fire('Error', 'เกิดข้อผิดพลาด', 'error');
        }
    };

    // Drag and Drop State
    const [draggedItemIndex, setDraggedItemIndex] = useState(null);

    const handleDragStart = useCallback((index) => (e) => {
        setDraggedItemIndex(index);
        e.dataTransfer.effectAllowed = "move";
    }, []);

    const handleDragOver = useCallback((index) => (e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
    }, []);

    const handleDrop = useCallback((index) => (e) => {
        e.preventDefault();
        const newRight = [...right];
        const draggedItem = newRight[draggedItemIndex];
        newRight.splice(draggedItemIndex, 1);
        newRight.splice(index, 0, draggedItem);
        setRight(newRight);
        setDraggedItemIndex(null);
    }, [right, draggedItemIndex]);

    // Search Filter State
    const [leftFilterText, setLeftFilterText] = useState("");
    const [rightFilterText, setRightFilterText] = useState("");

    // derived state for available years (rerender-derived-state)
    const availableYears = useMemo(() =>
        [...new Set(eventList.map(item => item.year))].sort((a, b) => b - a),
        [eventList]
    );

    // derived state for available quarters based on selected year (rerender-derived-state)
    const availableQuarters = useMemo(() => eventList
        .filter(ev => ev.year === year)
        .map(ev => ev.quarter)
        .sort((a, b) => a - b),
        [eventList, year]
    );

    return (
        <div className={classes.root}>
            <Typography variant="h4" gutterBottom>
                ออกรายงานเปรียบเทียบค่าชี้วัดคุณภาพบริการระหว่างบริษัท
            </Typography>

            <Paper className={classes.paper}>
                <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            select
                            label="ปี"
                            value={year}
                            onChange={(e) => {
                                const newYear = e.target.value
                                setYear(newYear)
                                // Reset quarter if needed
                                const quartersInNewYear = eventList.filter(ev => ev.year === newYear).map(ev => ev.quarter)
                                if (quartersInNewYear.length > 0 && !quartersInNewYear.includes(quarter)) {
                                    setQuarter(quartersInNewYear[0])
                                }
                            }}
                            fullWidth
                            variant="outlined"
                        >
                            {availableYears.map((option) => (
                                <MenuItem key={option} value={option}>
                                    {toThaiYear(option)}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            select
                            label="ไตรมาส"
                            value={quarter}
                            onChange={(e) => setQuarter(e.target.value)}
                            fullWidth
                            variant="outlined"
                        >
                            {availableQuarters.map((option) => (
                                <MenuItem key={option} value={option}>
                                    {option}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                </Grid>
            </Paper>

            <Grid container spacing={2} justify="center" alignItems="center" className={classes.root}>
                <Grid item>
                    <TransferList
                        title="ผู้ประกอบการที่ไม่เลือก"
                        items={left}
                        currentFilterText={leftFilterText}
                        setFilterFunc={setLeftFilterText}
                        enableDrag={false}
                        loading={loadingResult}
                        checked={checked}
                        handleToggle={handleToggle}
                        handleToggleAll={handleToggleAll}
                        numberOfChecked={numberOfChecked}
                    />
                </Grid>
                <Grid item>
                    <Grid container direction="column" alignItems="center">
                        <Button
                            variant="outlined"
                            size="small"
                            className={classes.button}
                            onClick={handleCheckedRight}
                            disabled={leftChecked.length === 0}
                            aria-label="เพิ่มผู้ประกอบการ"
                        >
                            &gt;
                        </Button>
                        <Button
                            variant="outlined"
                            size="small"
                            className={classes.button}
                            onClick={handleCheckedLeft}
                            disabled={rightChecked.length === 0}
                            aria-label="ลบผู้ประกอบการ"
                        >
                            &lt;
                        </Button>
                    </Grid>
                </Grid>
                <Grid item>
                    <TransferList
                        title="ผู้ประกอบการที่เลือก"
                        items={right}
                        currentFilterText={rightFilterText}
                        setFilterFunc={setRightFilterText}
                        enableDrag={true}
                        loading={loadingResult}
                        checked={checked}
                        handleToggle={handleToggle}
                        handleToggleAll={handleToggleAll}
                        handleDragStart={handleDragStart}
                        handleDragOver={handleDragOver}
                        handleDrop={handleDrop}
                        draggedItemIndex={draggedItemIndex}
                        numberOfChecked={numberOfChecked}
                    />
                </Grid>
            </Grid>

            {/* ... rest of the render (templates list) ... */}
            <Paper className={classes.paper} style={{ marginTop: 20 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant="h6" gutterBottom>
                        เลือกค่าชี้วัดคุณภาพบริการ
                    </Typography>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={templates.length > 0 && selectedTemplates.length === templates.length}
                                indeterminate={selectedTemplates.length > 0 && selectedTemplates.length < templates.length}
                                onChange={handleSelectAllTemplates}
                                color="primary"
                            />
                        }
                        label="เลือกทั้งหมด"
                    />
                </div>
                <List style={{ maxHeight: 300, overflow: 'auto' }}>
                    {templates.map((template) => (
                        <ListItem key={template.no} button onClick={handleTemplateToggle(template.no)}>
                            <ListItemIcon>
                                <Checkbox
                                    edge="start"
                                    checked={selectedTemplates.indexOf(template.no) !== -1}
                                    tabIndex={-1}
                                    disableRipple
                                />
                            </ListItemIcon>
                            <ListItemText primary={`${template.no} ${template.label}`} />
                        </ListItem>
                    ))}
                </List>
            </Paper>

            {/* Loading Overlay or Indicator */}
            {loadingResult && (
                <div style={{ display: 'flex', justifyContent: 'center', margin: '20px 0' }}>
                    <CircularProgress />
                    <Typography variant="body1" style={{ marginLeft: 10 }}>กำลังโหลดข้อมูล...</Typography>
                </div>
            )}

            <Grid container spacing={2}>
                <Grid item xs={6}>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleExport}
                        size="large"
                        fullWidth
                    >
                        ดาวน์โหลดรายงาน (PDF)
                    </Button>
                </Grid>
                <Grid item xs={6}>
                    <Button
                        variant="contained"
                        style={{ backgroundColor: '#2e7d32', color: 'white' }}
                        onClick={handleExportExcel}
                        size="large"
                        fullWidth
                    >
                        ดาวน์โหลดรายงาน (Excel)
                    </Button>
                </Grid>
            </Grid>
        </div>
    );
}
